"use client";

import { createInstance } from "@amplitude/analytics-browser";
import { type Metadata, type RichEvent } from "@stone-payments/analitica";
import { commonEnvs } from "@/config/env";
import { ExperimentsFlags } from "@/services/amplitude/experiment";

declare global {
  interface Window {
    analitica?: AmplitudeClientPlugin;
  }
}

type BrowserClient = ReturnType<typeof createInstance>;

export type AmplitudeClientOptions = {
  deviceId: string;
  userId?: string;
};

export type AmplitudeClientExperimentEvent = {
  flagKey: ExperimentsFlags;
  variantKey: string;
};

class AmplitudeClientPlugin {
  name = "SWH_ECOMMERCE_AMPLITUDE";
  instance?: BrowserClient;
  storeId?: string;

  constructor() {
    if (typeof window === "undefined") return;

    this.instance = createInstance();

    window.analitica = this;
  }

  init({ deviceId, userId }: AmplitudeClientOptions) {
    if (commonEnvs.ANALITICA_ENABLED) {
      this.instance?.init(commonEnvs.KEY.AMPLITUDE!, userId, {
        deviceId,
        appVersion: commonEnvs.VERSION,
        instanceName: "swh-ecommerce",
        autocapture: {
          pageViews: false,
        },
      });
    }
  }

  sendEvent(event: RichEvent): void {
    const { metadata, label, ...rest } = event;
    const { owner, product, project, storeId, version } =
      this.getCustomMetadata();

    this.instance?.track(label, {
      owner,
      product,
      project,
      storeId,
      version,
      ...metadata,
      ...rest,
    });
  }

  getCustomMetadata() {
    return {
      owner: "Commerce",
      product: "Vitrine de produtos",
      project: global.window?.location?.hostname?.includes("stone.com.br")
        ? "Stone"
        : "Ton",
      storeId: this.getStoreId(),
      version: "V.1",
    };
  }

  setStoreId(storeId: string) {
    this.storeId = storeId;
  }

  getStoreId() {
    return (
      this.storeId ||
      (typeof window !== "undefined" ? window.analitica?.storeId : "") ||
      ""
    );
  }

  updateMetadata(newMetadata: Metadata) {
    this.instance?.setUserId(newMetadata.user?.id);
  }

  sendExposureEvent(event: AmplitudeClientExperimentEvent) {
    const deviceId = this.instance?.getDeviceId();

    if (!deviceId) return;

    if (commonEnvs.ANALITICA_ENABLED && event.variantKey !== "default") {
      this.instance?.track(
        "$exposure",
        {
          flag_key: event.flagKey,
          variant: event.variantKey,
          storeId: this.getStoreId(),
        },
        {
          device_id: deviceId,
          user_id: this.instance?.getUserId(),
          session_id: this.instance?.getSessionId(),
        }
      );
    }
  }
}

export { AmplitudeClientPlugin };
